import { useParams } from '@tanstack/react-router'
import { TableDataQa } from 'commons/types/enums'
import { CircularProgress, FeedbackWidget } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button, IconButton } from 'ui/components/Buttons'
import { IconName } from 'ui/components/Icon'
import { PageV2 } from 'ui/components/Page'
import { useQRDetails } from '~bank-quarterly-report/hooks/qr'

import { QRSideBar } from '../../components'
import { HistorySection } from '../../components/HistorySection'
import { UploadedFilesTableWebPage } from '../../components/UploadedFilesTableWebPage'
import {
  BankAccountData,
  Footer,
  GeneralInformation,
  MonthlyReportsDialog,
  QuestionnaireForm,
  Warnings
} from './components'
import { defaultQuestionnaire } from './helpers'

const QRDetails = () => {
  const { id } = useParams({ from: '/_app/qr/$id' })

  const {
    webPageData,
    isFetching,
    uploadedFilesTableState,
    uploadedFilesChangePagination: changePagination,
    openReview,
    ReviewConfirmation,
    openWarnings,
    WarningsConfirmation,
    openDelete,
    DeleteConfirmation,
    onSetQuestionnaire,
    saveNotes,
    monthlyOpen,
    setMonthlyOpen,
    historyPagination,
    invalidate
  } = useQRDetails(+id)

  if (!webPageData) {
    return <CircularProgress centered />
  }

  const isShowWarning = !!webPageData?.infoWarnings && webPageData?.infoWarnings.length > 0

  return (
    <>
      <PageV2
        face="secondary"
        subTitle="Quarterly Analytics"
        actions={
          <ButtonGroup margin="big">
            <IconButton icon={IconName.DELETE} face="neutral" onClick={openDelete}>
              Delete QA
            </IconButton>
            {webPageData?.enableAcknowledgeAllWarnings && (
              <Button face="inherit white" onClick={openWarnings}>
                Acknowledge all Warnings
              </Button>
            )}
          </ButtonGroup>
        }
      >
        <QRSideBar />
        {isShowWarning && <Warnings warnings={webPageData?.infoWarnings} title="Common Warning(s)" />}
        <DeleteConfirmation />
        <ReviewConfirmation />
        <WarningsConfirmation />
        <GeneralInformation item={webPageData} openMonthly={() => setMonthlyOpen(true)} onSaveNotes={saveNotes} />
        <BankAccountData item={webPageData} />
        <UploadedFilesTableWebPage
          loading={isFetching}
          uploadedFiles={webPageData?.uploadedFiles}
          uploadedFilesTableState={uploadedFilesTableState}
          uploadedFilesTotalCount={webPageData?.uploadedFilesTotalCount}
          changePagination={changePagination}
          reportQuarterlyId={+id}
          invalidate={invalidate}
        />
        <HistorySection
          tableDataQa={TableDataQa.BP_QUARTERLY_ANALYTICS_HISTORY}
          loading={isFetching}
          historyItems={webPageData?.history}
          historyPagination={historyPagination}
        />
        <MonthlyReportsDialog
          open={monthlyOpen}
          onClose={() => setMonthlyOpen(false)}
          reportsMonthly={webPageData?.monthlyAnalytics}
          reportPeriod={webPageData?.infoPeriod}
        />
        {webPageData?.enableQuestionnaire && (
          <QuestionnaireForm
            setQuestionnaire={onSetQuestionnaire}
            questionnaire={webPageData?.showQuestionnaire || defaultQuestionnaire}
          />
        )}
        <FeedbackWidget contentPage="Quarterly Analytics Details" />
      </PageV2>
      <Footer onReview={webPageData?.enableReview ? openReview : undefined} sticky />
    </>
  )
}

export default QRDetails
